import { useButton as useAriaButton, type RNAriaButtonProps } from '@react-native-aria/button'
import { useFocusRing } from '@react-native-aria/focus'
import { useHover } from '@react-native-aria/interactions'
import { mergeProps } from '@react-native-aria/utils'
import clsx from 'clsx'
import { useCallback, useMemo, type ReactNode } from 'react'
import { View, type GestureResponderEvent, type PressableProps } from 'react-native'

import { Pressable } from '@/src/elements/pressable'
import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { cardVariants, type TCardSlots, type TCardVariantProps, type TCardReturnType } from './card-variants'

type TProps = {
  as?: React.ElementType
  additionalProps?: Record<string, unknown>
  testID?: string

  /**
   * Usually the Card parts, `CardHeader`, `CardBody` and `CardFooter`.
   */
  children?: ReactNode

  className?: string

  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <Card classNames={{
   *    base:"base-classes",
   *    header: "dot-classes",
   *    body: "content-classes",
   *    footer: "avatar-classes",
   * }} />
   * ```
   */
  isPressable?: boolean
  classNames?: SlotsToClasses<TCardSlots>

  onPress?: (event: GestureResponderEvent) => void

  /**
   * Ref to the DOM node.
   */
  ref?: React.Ref<View>
}

export type TUseCardProps = TProps & TCardVariantProps

export type TContextType = {
  slots: TCardReturnType
  classNames?: SlotsToClasses<TCardSlots>
} & Pick<TCardVariantProps, 'isDisabled' | 'fullWidth'>

export function useCard(originalProps: TUseCardProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, cardVariants.variantKeys)

  const { as, additionalProps, testID, children, onPress, className, classNames, ...rest } = props

  const Component = as || (originalProps.isPressable ? Pressable : View)

  const baseStyles = clsx(classNames?.base, className)

  const { buttonProps: ariaButtonProps, isPressed } = useAriaButton({
    isDisabled: !originalProps.isPressable,
    onPress,
    ...rest,
  } as RNAriaButtonProps)

  const { hoverProps, isHovered } = useHover({
    isDisabled: !originalProps.isHoverable,
    ...rest,
  })

  const { isFocusVisible, focusProps } = useFocusRing()

  const slots = cardVariants({ ...variantProps })

  const context = useMemo<TContextType>(
    () => ({
      isDisabled: originalProps.isDisabled,
      fullWidth: originalProps.fullWidth,
      slots,
      classNames,
    }),
    [slots, classNames, originalProps.isDisabled, originalProps.fullWidth],
  )

  const getCardProps = useCallback<PropGetter<PressableProps>>(() => {
    return {
      className: slots.base({ class: baseStyles }),
      tabIndex: originalProps.isPressable ? 0 : -1,
      dataSet: {
        hover: isHovered,
        pressed: isPressed,
        'focus-visible': isFocusVisible,
        disabled: originalProps.isDisabled,
      },
      ...mergeProps(
        originalProps.isPressable ? { ...ariaButtonProps, ...focusProps, role: 'button' } : {},
        originalProps.isHoverable ? hoverProps : {},
      ),
      ...additionalProps,
    }
  }, [
    additionalProps,
    slots,
    baseStyles,
    originalProps.isPressable,
    originalProps.isDisabled,
    originalProps.isHoverable,
    isHovered,
    isPressed,
    isFocusVisible,
    ariaButtonProps,
    focusProps,
    hoverProps,
  ])

  return {
    context,
    dataTestId: testID,
    Component,
    classNames,
    children,
    isHovered,
    isPressed,
    isPressable: originalProps.isPressable,
    isHoverable: originalProps.isHoverable,
    isFocusVisible,
    getCardProps,
  }
}
